/**
 * TransitionCatalog Repository
 *
 * Manages reusable transition templates for workflow state machines
 * FERP SAFETY: Critical for maintaining consistent state transition logic
 */

import { BaseRepository } from './BaseRepository';
import { TransitionTemplate, TransitionAction } from '../types/WorkflowEntities';

class TransitionCatalogRepository extends BaseRepository<TransitionTemplate> {
    protected entityName = 'transitionTemplate';
    protected graphQLType = 'TransitionTemplate';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<TransitionTemplate | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'TransitionTemplate',
            criticalityLevel: 'HIGH', // Transitions are critical for state machine logic
            metadata: { id, transitionCatalogRepository: true }
        });

        const query = `
            query GetTransitionTemplateByTenantAndId($id: String!, $tenant: String!) {
                transitionTemplate(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ transitionTemplate: TransitionTemplate | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 10 * 60 * 1000 // 10 minutes cache for transition templates
            });

            const template = result.transitionTemplate || null;

            // ✅ FERP SAFETY: Log successful operation with transition details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'TransitionTemplate',
                    entityId: id,
                    found: !!template,
                    fromState: template?.fromState,
                    toState: template?.toState,
                    category: template?.category,
                    timestamp: Date.now()
                });
            }

            return template;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'TransitionTemplate',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(templateId: string): Promise<TransitionTemplate | null> {
        return this.findByTenantAndId(this.tenant, templateId);
    }

    // Transition Catalog-specific methods

    /**
     * Find transitions by state pair
     * FERP SAFETY: Critical for building valid state machine graphs
     */
    async findByStatePair(fromState: string, toState: string): Promise<TransitionTemplate[]> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_STATE_PAIR',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'TransitionTemplate',
            criticalityLevel: 'HIGH',
            metadata: { fromState, toState }
        });

        const query = `
            query GetTransitionsByStatePair(
                $fromState: String!,
                $toState: String!,
                $tenant: String!
            ) {
                transitionsByStatePair(
                    fromState: $fromState,
                    toState: $toState,
                    tenant: $tenant
                ) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            fromState,
            toState,
            tenant: this.tenant
        });

        return result.transitionsByStatePair || [];
    }

    /**
     * Find all transitions from a given state
     * FERP SAFETY: Used for dynamic transition discovery in workflows
     */
    async findByFromState(fromState: string): Promise<TransitionTemplate[]> {
        const query = `
            query GetTransitionsByFromState($fromState: String!, $tenant: String!) {
                transitionsByFromState(fromState: $fromState, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            fromState,
            tenant: this.tenant
        });

        return result.transitionsByFromState || [];
    }

    /**
     * Find transitions by category
     * FERP SAFETY: Group related transitions for UI organization
     */
    async findByCategory(category: string): Promise<TransitionTemplate[]> {
        const query = `
            query GetTransitionsByCategory($category: String!, $tenant: String!) {
                transitionsByCategory(category: $category, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            category,
            tenant: this.tenant
        });

        return result.transitionsByCategory || [];
    }

    /**
     * Get all available categories
     * FERP SAFETY: Data-driven category discovery
     */
    async getCategories(): Promise<string[]> {
        const query = `
            query GetTransitionCategories($tenant: String!) {
                transitionCategories(tenant: $tenant)
            }
        `;

        const result = await this.executeQuery(query, {
            tenant: this.tenant
        });

        return result.transitionCategories || [];
    }

    /**
     * Find most used transitions
     * FERP SAFETY: Identify common patterns for optimization
     */
    async findMostUsed(limit?: number): Promise<TransitionTemplate[]> {
        const query = `
            query GetMostUsedTransitions($limit: Int, $tenant: String!) {
                mostUsedTransitions(limit: $limit, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {
            limit: limit || 10,
            tenant: this.tenant
        });

        return result.mostUsedTransitions || [];
    }

    /**
     * Create new transition template
     * FERP SAFETY: Validates transition logic before creation
     */
    async createTemplate(input: {
        name: string;
        description: string;
        fromState: string;
        toState: string;
        condition?: string;
        actions: TransitionAction[];
        category: string;
    }): Promise<TransitionTemplate> {
        await this.validateRepositorySafety({
            operation: 'CREATE_TEMPLATE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'TransitionTemplate',
            criticalityLevel: 'HIGH',
            metadata: { input }
        });

        const mutation = `
            mutation CreateTransitionTemplate($input: CreateTransitionTemplateInput!) {
                createTransitionTemplate(input: $input) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            input: {
                ...input,
                tenant: this.tenant
            }
        });

        return result.createTransitionTemplate;
    }

    /**
     * Update transition template
     * FERP SAFETY: Validates changes before updating
     */
    async updateTemplate(templateId: string, updates: Partial<{
        name: string;
        description: string;
        condition: string;
        actions: TransitionAction[];
        category: string;
    }>): Promise<TransitionTemplate> {
        await this.validateRepositorySafety({
            operation: 'UPDATE_TEMPLATE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'TransitionTemplate',
            criticalityLevel: 'HIGH',
            metadata: { templateId, updates }
        });

        const mutation = `
            mutation UpdateTransitionTemplate($templateId: ID!, $updates: UpdateTransitionTemplateInput!) {
                updateTransitionTemplate(templateId: $templateId, updates: $updates) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            templateId,
            updates: {
                ...updates,
                tenant: this.tenant
            }
        });

        return result.updateTransitionTemplate;
    }

    /**
     * Delete transition template
     * FERP SAFETY: Validates no active usage before deletion
     */
    async deleteTemplate(templateId: string): Promise<{ success: boolean; message: string }> {
        await this.validateRepositorySafety({
            operation: 'DELETE_TEMPLATE',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'TransitionTemplate',
            criticalityLevel: 'HIGH',
            metadata: { templateId }
        });

        const mutation = `
            mutation DeleteTransitionTemplate($templateId: ID!, $tenant: String!) {
                deleteTransitionTemplate(templateId: $templateId, tenant: $tenant) {
                    success
                    message
                }
            }
        `;

        const result = await this.executeQuery(mutation, {
            templateId,
            tenant: this.tenant
        });

        return result.deleteTransitionTemplate;
    }

    /**
     * Validate transition template
     * FERP SAFETY: Pre-validation before applying to workflows
     */
    async validateTemplate(template: {
        fromState: string;
        toState: string;
        condition?: string;
        actions: TransitionAction[];
    }): Promise<{
        valid: boolean;
        errors: string[];
        warnings: string[];
    }> {
        const query = `
            query ValidateTransitionTemplate($template: TransitionTemplateInput!, $tenant: String!) {
                validateTransitionTemplate(template: $template, tenant: $tenant) {
                    valid
                    errors
                    warnings
                }
            }
        `;

        const result = await this.executeQuery(query, {
            template: {
                ...template,
                tenant: this.tenant
            },
            tenant: this.tenant
        });

        return result.validateTransitionTemplate || {
            valid: false,
            errors: ['Validation service unavailable'],
            warnings: []
        };
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            description
            fromState
            toState
            condition
            actions {
                type
                config
                order
            }
            category
            usageCount
            createdAt
            updatedAt
        `;
    }
}

export const transitionCatalogRepository = new TransitionCatalogRepository();
export { TransitionCatalogRepository };
